import React from 'react'
import { $createTextNode, $getRoot } from 'lexical'
import {
    $convertFromMarkdownString,
    type TextMatchTransformer,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    CHECK_LIST,
    HEADING,
    HIGHLIGHT,
    INLINE_CODE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    LINK,
    ORDERED_LIST,
    QUOTE,
    STRIKETHROUGH,
    UNORDERED_LIST,
} from '@lexical/markdown'
import type { LexicalEditor } from 'lexical/LexicalEditor'
import { LexicalComposer, type InitialConfigType } from '@lexical/react/LexicalComposer'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { CodeNode } from '@lexical/code'
import { LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { NodeEventPlugin } from '@lexical/react/LexicalNodeEventPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { getSourceReferences } from '../../../shared/domain/aiChats/helpers.no-deps'
import { doNothing } from '../../../shared/lib/functional/function.no-deps'
import { RefPlugin } from '../lexical/plugins/RefPlugin'
import { $createReferenceNode, ReferenceNode } from './lexical/ReferenceNode'
import { $createCopyButtonNode, CopyButtonNode } from './lexical/CopyButtonNode'
import type { AiChatSource } from '../../../shared/domain/aiChats/types.no-deps'

type ZapBotAnswerRendererProps = {
    content: string
    sources: AiChatSource[]
    isLoading: boolean
    allowAnswerCopy: boolean
    onClickCopyButton: () => void
}

const LEXICAL_CONFIG: InitialConfigType = {
    namespace: 'ZapBot',
    editable: false,
    onError: doNothing,
    theme: {
        root: 'ZapBot_answer_lexical',
    },
    nodes: [
        HorizontalRuleNode,
        CodeNode,
        LinkNode,
        ListNode,
        ListItemNode,
        HeadingNode,
        QuoteNode,
        ReferenceNode,
        CopyButtonNode,
    ],
}

export const ZapBotAnswerRenderer = React.forwardRef<LexicalEditor | null, ZapBotAnswerRendererProps>(
    (props: ZapBotAnswerRendererProps) => {
        const editorRef = React.useRef<LexicalEditor>()

        const CUSTOM_LINK: TextMatchTransformer = {
            ...LINK,
            replace: (textNode, match) => {
                const [, linkText, linkUrl] = match
                const parentNode = textNode.getParent()

                // Renders MD links as bold text.
                const linkTextNode = $createTextNode(linkText).setFormat('bold')
                textNode.replace(linkTextNode)

                if (!props.sources.length) {
                    return // Don't try to add reference numbers if sources aren't available yet.
                }

                const [parsedSource] = getSourceReferences(linkUrl)
                if (!parsedSource) {
                    return // The parsed MD link isn't a valid source link.
                }

                const sourceIndex = props.sources.findIndex(
                    (entry) => entry.type === parsedSource.type && entry.entityId?.toString() === parsedSource.id
                )
                if (sourceIndex === -1) {
                    return // The parsed source isn't one of the sources parsed on the backend.
                }

                const referenceWithWarningNode = parentNode
                    .getChildren()
                    .find((node) => node instanceof ReferenceNode && node.isWarningVisible())

                const isReportSource = parsedSource.type === 'report'
                const referenceNode = $createReferenceNode(
                    sourceIndex + 1,
                    isReportSource ? 'warning' : 'normal',
                    !referenceWithWarningNode && isReportSource
                )

                if (referenceWithWarningNode) {
                    referenceWithWarningNode.insertBefore(referenceNode)
                } else {
                    parentNode.append(referenceNode)
                }
            },
        }

        const transformers = [
            CUSTOM_LINK,
            BOLD_ITALIC_STAR,
            BOLD_ITALIC_UNDERSCORE,
            BOLD_STAR,
            BOLD_UNDERSCORE,
            CHECK_LIST,
            HEADING,
            HIGHLIGHT,
            INLINE_CODE,
            ITALIC_STAR,
            ITALIC_UNDERSCORE,
            ORDERED_LIST,
            QUOTE,
            STRIKETHROUGH,
            UNORDERED_LIST,
        ]

        // Hook to continuously parse the answer as it's streamed.
        React.useEffect(() => {
            if (editorRef.current) {
                editorRef.current.update(() => {
                    $convertFromMarkdownString(props.content, transformers)
                })
            }
        }, [props.content, props.sources])

        // Hook to add the copy-button to the end of the answer.
        React.useEffect(() => {
            if (!editorRef.current || props.isLoading || !props.allowAnswerCopy) {
                return
            }

            editorRef.current.update(() => {
                const root = $getRoot()
                const lastTextNode = root.getAllTextNodes().slice(-1)[0]

                const copyButtonNode = $createCopyButtonNode()
                if (!lastTextNode) {
                    return
                }

                lastTextNode.getParent().append(copyButtonNode)
            })
        }, [props.isLoading])

        return (
            <LexicalComposer initialConfig={LEXICAL_CONFIG}>
                <RichTextPlugin
                    contentEditable={<ContentEditable />}
                    placeholder={<></>}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <MarkdownShortcutPlugin transformers={transformers} />
                <NodeEventPlugin nodeType={CopyButtonNode} eventType="click" eventListener={props.onClickCopyButton} />
                <RefPlugin ref={editorRef} />
            </LexicalComposer>
        )
    }
)
